import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { useAppContext } from "./useAppContext";
import useToast from "./useToast";

export default function useDonationsForm({ item, isMiniCart, onSubmitItem }) {
  const customAmountInputName = `donation_amount${item.id}`;
  const itemPrice = item.attributes.total_price?.number;
  const initialPrice = itemPrice && parseFloat(itemPrice).toFixed(2);
  const {
    register,
    handleSubmit,
    setValue,
    formState: {
      errors,
    },
    getValues,
    unregister,
    clearErrors,
    setError,
    watch,
  } = useForm({
    mode: "onChange",
    defaultValues: {
      [customAmountInputName]: initialPrice,
    },
  });

  const [isLoadingCustomAmount, setIsLoadingCustomAmount] = useState(false);
  const [selectedAmount, setSelectedAmount] = useState(initialPrice);
  const [customAmountFocus, setCustomAmountFocus] = useState(false);
  const [submitActive, setSubmitActive] = useState(false);
  const { items, setTotal, setItems, setLoadingItems, loadingItems } =
    useAppContext();
  const { frequency, setEditStateItems } = useAppContext();
  const customAmount = watch(customAmountInputName);
  const isItemLoading = useMemo(
    () => loadingItems.includes(item.id) || isLoadingCustomAmount,
    [loadingItems, isLoadingCustomAmount]
  );
  const totals = useMemo(
    () => items.map(getPrices),
    [items, selectedAmount, customAmount, customAmountFocus]
  );

  const hasErrors = useMemo(() => {
    const hasFormErrors = Object.keys(errors).length > 0;
    return (
      (!selectedAmount && !customAmount) || (hasFormErrors && !submitActive)
    );
  }, [selectedAmount, submitActive, customAmount, customAmountFocus, errors]);

  const total = useMemo(() => {
    const grandTotal = totals.reduce((acc, item) => {
      return acc + item.price;
    }, 0);
    return `$${parseFloat(grandTotal).toLocaleString(undefined, {minimumFractionDigits: 2})}`;
  }, [totals]);

  const fixedValueAmounts = useMemo(() => {
    // @todo structure frequency data consistently from server-side.
    return (
      item.attributes[`field_${frequency}`]?.split("\r\n") ||
      item.attributes[frequency]
    );
  }, [isMiniCart, item, frequency]);

  function getPrices({ id = "", attributes }) {
    if (id === item.id && (selectedAmount || customAmount)) {
      return {
        id,
        price:
          parseFloat(customAmountFocus ? customAmount : selectedAmount) || 0,
      };
    }
    return attributes
      ? { id, price: parseFloat(attributes.total_price.number) }
      : {};
  }

  function setItemPrice(price) {
    const newItems = items.map((i) => {
      if (i.id === item.id) {
        return {
          ...i,
          attributes: {
            ...i.attributes,
            total_price: {
              ...i.attributes.total_price,
              formatted: `$${parseFloat(price).toLocaleString(undefined, {minimumFractionDigits: 2})}`,
              number: price,
            },
          },
        };
      }
      return i;
    });
    setItems(newItems);
  }

  const getNotifySubmitItemError = useCallback(
    (action) => {
      return useToast({
        type: "error",
        id: "submitItem",
        actionText: "RETRY?",
        action: async () => await action(),
        dismissable: true,
        message: "There was a problem updating your gift.",
      });
    },
    [setAmount, onClickAmount]
  );

  async function setAmount(amount) {
    const notifySubmitItemError = getNotifySubmitItemError(() =>
      setAmount(amount)
    );
    setSelectedAmount(amount);
    setCustomAmountFocus(null);
    setValue(customAmountInputName, null);
    clearErrors(customAmountInputName);
    if (!isMiniCart) {
      return onSubmitItem(item, amount)
        .catch((e) => {
          setAmounts(amount);
          notifySubmitItemError();
          return Promise.reject(e);
        })
        .then((e) => {
          setItemPrice(parseFloat(amount).toFixed(2));
          setEditStateItems((editItems) =>
            editItems.filter((i) => i !== item.id)
          );
          return e;
        });
    }
    setValue("donation_amount" + item.id, null);
  }

  const onClickAmount = async (amount) => {
    const notifySubmitItemError = getNotifySubmitItemError(() =>
      onClickAmount(amount)
    );

    setLoadingItems((loadingItems) => [...loadingItems, item.id]);
    return setAmount(amount)
      .then(() => {
        toast.dismiss("submitItem");
        setItemPrice(parseFloat(amount).toFixed(2));
        setEditStateItems((editItems) =>
          editItems.filter((i) => i !== item.id)
        );
        return true;
      })
      .catch((e) => {
        setSelectedAmount(selectedAmount);
        setIsLoadingCustomAmount(false);
        notifySubmitItemError();
        return Promise.reject(e);
      })
      .finally(() => {
        setLoadingItems((loadingItems) =>
          loadingItems.filter((i) => i !== item.id)
        );
      });
  };

  useEffect(() => {
    if (!itemPrice) setCustomAmountFocus(null);
  }, [itemPrice]);

  useEffect(() => {
    if (selectedAmount === "0.00") {
      setValue(customAmountInputName, null);
    }
  }, [customAmountFocus]);

  useEffect(() => {
    setAmounts();
  }, [selectedAmount, frequency]);

  useEffect(() => {
    if (!errors[`donation_amount${item.id}`]) {
      setTotal(total);
    }
    if (customAmount) {
      setSubmitActive(true);
    }
  }, [totals]);

  useEffect(() => {
    if (!customAmountFocus) {
      unregister(customAmountInputName);
    }
  }, [customAmountFocus]);
  const setAmounts = () => {
    const values = getValues();
    const value = values[customAmountInputName];
    const customAmount = value
      ? (Math.round(value * 100) / 100).toFixed(2)
      : "";
    const amount = customAmountFocus
      ? customAmount
      : selectedAmount || initialPrice;
    const matchesFrequency = fixedValueAmounts
      ?.map((i) => parseFloat(i, 10))
      .includes(parseFloat(amount, 10));

    if (matchesFrequency) {
      setCustomAmountFocus(false);
      setValue("donation_amount" + item.id, null);
      setSelectedAmount(parseInt(amount, 10));
    }
    if (amount > 0 && !matchesFrequency) {
      setCustomAmountFocus(true);
      setValue("donation_amount" + item.id, amount);
    }
  };

  const customAmountProps = {
    register,
    handleSubmit,
    errors,
    setSubmitActive,
    submitActive,
  };

  return {
    onClickAmount,
    fixedValueAmounts,
    customAmountFocus,
    hasErrors,
    customAmount,
    frequency,
    setCustomAmountFocus,
    selectedAmount,
    customAmountProps,
    getValues,
    setError,
    isItemLoading,
    clearErrors,
    isLoadingCustomAmount,
    setIsLoadingCustomAmount,
    setItemPrice,
  };
}
